import { useState, useEffect, useRef } from 'react';
import { getDatabase, ref, get, set, onValue, off } from 'firebase/database';
import { FaTimes, FaRupeeSign, FaClock, FaWhatsapp } from 'react-icons/fa';

/**
 * PaymentGateModal
 *
 * Firebase nodes written on submission:
 *   Matrimony/users/{phone}/registrationPayment   — user-scoped copy
 *   Matrimony/registrationPayments/{phone}        — dedicated index for fast admin reads
 *
 * Admin flips status → onValue listener fires instantly → user advances to profile form.
 */
const PaymentGateModal = ({ isOpen, phoneNumber, onPaymentApproved, onClose }) => {
    const [qrData, setQrData]           = useState(null);
    const [transactionId, setTransactionId] = useState('');
    const [initialLoading, setInitialLoading] = useState(true);
    const [submitting, setSubmitting]   = useState(false);
    const [submitted, setSubmitted]     = useState(false);
    const [error, setError]             = useState('');
    const [paymentStatus, setPaymentStatus] = useState(null); // null | 'pending' | 'approved' | 'rejected'
    const listenerRef = useRef(null);

    // ── Load QR code once ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;
        const db = getDatabase();
        get(ref(db, 'Matrimony/Qr'))
            .then(snap => { if (snap.exists()) setQrData(snap.val()); })
            .catch(e => console.error('QR load error:', e));
    }, [isOpen]);

    // ── Attach realtime listener to the dedicated payment node ─────────────────
    useEffect(() => {
        if (!isOpen || !phoneNumber) return;

        const db = getDatabase();
        // Primary fast node
        const payRef = ref(db, `Matrimony/registrationPayments/${phoneNumber}`);

        const handleSnapshot = (snap) => {
            setInitialLoading(false);
            if (!snap.exists()) return; // no payment yet — show form

            const data = snap.val();
            setPaymentStatus(data.status);

            if (data.status === 'pending') {
                setSubmitted(true);
            } else if (data.status === 'approved') {
                // Detach listener before calling callback
                off(payRef, 'value', handleSnapshot);
                onPaymentApproved();
            } else if (data.status === 'rejected') {
                setSubmitted(false);
            }
        };

        listenerRef.current = handleSnapshot;
        onValue(payRef, handleSnapshot, (err) => {
            console.error('Listener error:', err);
            setInitialLoading(false);
        });

        return () => {
            off(payRef, 'value', handleSnapshot);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, phoneNumber]);

    // ── Submit payment details ─────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!transactionId.trim()) {
            setError('Please enter your UPI Transaction ID');
            return;
        }
        setSubmitting(true);
        try {
            const db = getDatabase();
            const payload = {
                isPaid: false,
                status: 'pending',
                amount: 999,
                transactionId: transactionId.trim(),
                submittedAt: new Date().toISOString(),
                phoneNumber,
            };
            // Write to both nodes simultaneously
            await Promise.all([
                set(ref(db, `Matrimony/users/${phoneNumber}/registrationPayment`), payload),
                set(ref(db, `Matrimony/registrationPayments/${phoneNumber}`), payload),
            ]);
            // Listener will pick up the change and set status to 'pending'
        } catch (e) {
            console.error('Submit error:', e);
            setError('Failed to submit. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={overlayStyle}>
            <div style={modalStyle}>
                {onClose && (
                    <button onClick={onClose} style={closeBtnStyle} aria-label="Close">
                        <FaTimes />
                    </button>
                )}

                {/* Header */}
                <div style={headerStyle}>
                    <div style={iconCircleStyle}>
                        <FaRupeeSign style={{ fontSize: '2rem', color: '#6a11cb' }} />
                    </div>
                    <h2 style={titleStyle}>Registration Fee</h2>
                    <p style={subtitleStyle}>
                        A one-time registration fee of <strong>₹999</strong> is required to
                        complete your profile and start browsing.
                    </p>
                </div>

                {/* ── States ── */}
                {initialLoading ? (
                    <div style={centeredStyle}>
                        <div style={spinnerStyle} />
                        <p style={{ color: '#64748b', marginTop: '1rem' }}>Checking payment status…</p>
                    </div>

                ) : submitted && paymentStatus === 'pending' ? (
                    /* Pending */
                    <div style={pendingBoxStyle}>
                        <FaClock style={{ fontSize: '3rem', color: '#f59e0b', marginBottom: '1rem' }} />
                        <h3 style={{ color: '#92400e', marginBottom: '0.5rem' }}>Payment Under Review</h3>
                        <p style={{ color: '#78350f', marginBottom: '1rem', lineHeight: 1.6 }}>
                            Your payment details have been submitted. Our team will verify and
                            approve within a few hours. This page will update <strong>automatically</strong> once approved.
                        </p>
                        <p style={{ fontSize: '0.82rem', color: '#a16207', marginBottom: '1rem' }}>
                            ⟳ Listening for approval in real-time…
                        </p>
                        <a
                            href={`https://wa.me/919271465748?text=Hi%2C%20I%20have%20paid%20the%20registration%20fee%20of%20%E2%82%B9999.%20My%20phone%20number%20is%20${phoneNumber}.%20Please%20approve%20my%20payment.`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={whatsappBtnStyle}
                        >
                            <FaWhatsapp style={{ marginRight: '0.5rem' }} />
                            Contact Support: +91 92714 65748
                        </a>
                    </div>

                ) : paymentStatus === 'rejected' ? (
                    /* Rejected */
                    <div style={{ ...pendingBoxStyle, background: '#fef2f2', border: '1px solid #fecaca' }}>
                        <FaTimes style={{ fontSize: '2.5rem', color: '#ef4444', marginBottom: '1rem' }} />
                        <h3 style={{ color: '#991b1b', marginBottom: '0.5rem' }}>Payment Rejected</h3>
                        <p style={{ color: '#7f1d1d', marginBottom: '1.5rem' }}>
                            Your previous payment was rejected. Please re-submit with the correct transaction ID.
                        </p>
                        <button
                            onClick={() => { setPaymentStatus(null); setSubmitted(false); setTransactionId(''); }}
                            style={submitBtnStyle}
                        >
                            Try Again
                        </button>
                    </div>

                ) : (
                    /* Payment form */
                    <>
                        <div style={amountBadgeStyle}>
                            <span style={{ fontSize: '2rem', fontWeight: 'bold', color: '#6a11cb' }}>₹999</span>
                            <span style={{ color: '#64748b', marginLeft: '0.5rem' }}>One-time Registration Fee</span>
                        </div>

                        {qrData ? (
                            <div style={qrBoxStyle}>
                                <p style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#1e293b' }}>
                                    Scan &amp; Pay via UPI
                                </p>
                                <img
                                    src={qrData.imageUrl}
                                    alt="UPI QR Code"
                                    style={{ width: '180px', height: '180px', objectFit: 'contain', borderRadius: '8px' }}
                                />
                                {qrData.merchantName && (
                                    <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
                                        Pay to: {qrData.merchantName}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div style={{ ...qrBoxStyle, color: '#64748b' }}>
                                <p>QR code not available. Please contact support.</p>
                                <a href="https://wa.me/919271465748" target="_blank" rel="noopener noreferrer"
                                    style={{ color: '#25D366', fontWeight: '600' }}>
                                    Contact on WhatsApp
                                </a>
                            </div>
                        )}

                        <div style={stepsStyle}>
                            <p style={stepStyle}><strong>1.</strong> Scan the QR code above and pay <strong>₹999</strong></p>
                            <p style={stepStyle}><strong>2.</strong> Note the UPI Transaction ID from your payment app</p>
                            <p style={stepStyle}><strong>3.</strong> Enter the Transaction ID below and submit</p>
                            <p style={stepStyle}><strong>4.</strong> Wait for admin approval (usually within a few hours)</p>
                        </div>

                        <form onSubmit={handleSubmit} style={{ marginTop: '1.5rem' }}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={labelStyle}>UPI Transaction ID *</label>
                                <input
                                    type="text"
                                    value={transactionId}
                                    onChange={(e) => setTransactionId(e.target.value)}
                                    placeholder="e.g. 123456789012"
                                    style={inputStyle}
                                    required
                                />
                            </div>
                            {error && <div style={errorStyle}>{error}</div>}
                            <button type="submit" style={submitBtnStyle} disabled={submitting}>
                                {submitting ? 'Submitting…' : 'Submit Payment Details'}
                            </button>
                        </form>

                        <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#94a3b8', marginTop: '1rem' }}>
                            Having trouble?{' '}
                            <a href="https://wa.me/919271465748" target="_blank" rel="noopener noreferrer"
                                style={{ color: '#6a11cb' }}>
                                Contact support: +91 92714 65748
                            </a>
                        </p>
                    </>
                )}
            </div>

            <style>{`
                @keyframes pgm-spin {
                    0%   { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

/* ── Styles ── */
const overlayStyle = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: '1rem',
};
const modalStyle = {
    background: '#fff', borderRadius: '16px', padding: '2rem',
    width: '100%', maxWidth: '480px', maxHeight: '90vh',
    overflowY: 'auto', position: 'relative',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};
const closeBtnStyle = {
    position: 'absolute', top: '1rem', right: '1rem',
    background: 'none', border: 'none', fontSize: '1.2rem',
    cursor: 'pointer', color: '#64748b',
};
const headerStyle  = { textAlign: 'center', marginBottom: '1.5rem' };
const iconCircleStyle = {
    width: '64px', height: '64px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 1rem',
};
const titleStyle    = { fontSize: '1.6rem', fontWeight: 'bold', color: '#1e293b', margin: '0 0 0.5rem' };
const subtitleStyle = { color: '#64748b', lineHeight: 1.6, margin: 0 };
const amountBadgeStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
    borderRadius: '12px', padding: '1rem', marginBottom: '1.5rem',
};
const qrBoxStyle = {
    textAlign: 'center', padding: '1.5rem',
    background: '#f8fafc', borderRadius: '12px',
    border: '1px solid #e2e8f0', marginBottom: '1rem',
};
const stepsStyle = {
    background: '#f0fdf4', borderRadius: '10px',
    padding: '1rem 1.25rem', border: '1px solid #bbf7d0',
};
const stepStyle  = { margin: '0.4rem 0', color: '#166534', fontSize: '0.9rem' };
const labelStyle = { display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.4rem', fontSize: '0.9rem' };
const inputStyle = {
    width: '100%', padding: '0.75rem 1rem',
    border: '2px solid #e2e8f0', borderRadius: '8px',
    fontSize: '1rem', outline: 'none', boxSizing: 'border-box',
};
const errorStyle = {
    background: '#fef2f2', color: '#dc2626',
    padding: '0.75rem', borderRadius: '8px',
    marginBottom: '1rem', fontSize: '0.9rem',
};
const submitBtnStyle = {
    width: '100%', padding: '0.9rem',
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: '#fff', border: 'none', borderRadius: '10px',
    fontSize: '1rem', fontWeight: '600', cursor: 'pointer',
};
const pendingBoxStyle = {
    textAlign: 'center', padding: '2rem',
    background: '#fffbeb', borderRadius: '12px',
    border: '1px solid #fde68a',
};
const whatsappBtnStyle = {
    display: 'inline-flex', alignItems: 'center',
    background: '#25D366', color: '#fff',
    padding: '0.75rem 1.5rem', borderRadius: '8px',
    textDecoration: 'none', fontWeight: '600', marginTop: '1rem',
};
const centeredStyle = { textAlign: 'center', padding: '2rem' };
const spinnerStyle  = {
    width: '40px', height: '40px', margin: '0 auto',
    border: '4px solid #e2e8f0', borderTop: '4px solid #6a11cb',
    borderRadius: '50%', animation: 'pgm-spin 0.8s linear infinite',
};

export default PaymentGateModal;
